import { GrantsPanel } from '../orbit-room/GrantsPanel';
import React, { useState } from 'react';
// import { useNavigate } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import clsx from 'clsx';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { OrbitToolBoard, OrbitToolRow, PartnerClientRow, PartnerOverview } from '../../types/aai';
import { Action, Drawer, ErrorNote, Kicker, Loading, StatusChip } from '../../components/aa';
import { STATUS_CHIP, STATUS_LABEL } from './format';

/*
 * The three client-facing settings sections a Solution Partner reads: how AI is
 * routed in the client's workspace, who is in it, and what each role may see.
 *
 * All three are read-only here. A partner prepares and recommends; the client
 * owner and their Admin / Builder hold the policy, the roster, and the grants.
 * The layout is the platform's settings design: an intro line, then one
 * bordered list whose rows open in place to show their facts.
 */

const KICKER = 'text-[11px] font-bold uppercase tracking-[0.06em] text-brand-700';

const SectionIntro: React.FC<{ title: string; body?: string }> = ({ title, body }) => (
  <div className="mb-5">
    <h2 className="m-0 text-[23px] font-bold leading-[1.2] tracking-[-0.03em] text-ink">{title}</h2>
    {body && <p className="m-0 mt-2 max-w-[720px] text-[13px] leading-[21px] text-ink-muted">{body}</p>}
  </div>
);

/* A switch the partner can see but not move: the client's policy, not theirs. */
const Switch: React.FC<{ on: boolean }> = ({ on }) => (
  <span
    role="switch"
    aria-checked={on}
    aria-disabled="true"
    className={clsx(
      'relative inline-block h-[22px] w-[38px] shrink-0 rounded-full transition-colors',
      on && 'bg-ink-soft',
      !on && 'bg-line-strong',
    )}
  >
    <span className={clsx(
      'absolute top-[3px] h-4 w-4 rounded-full bg-white shadow-[0_1px_3px_rgba(24,25,29,0.18)] transition-transform',
      on && 'left-[19px]',
      !on && 'left-[3px]',
    )} />
  </span>
);

const SwitchRow: React.FC<{ title: string; detail: string; on: boolean }> = ({ title, detail, on }) => (
  <div className="flex min-h-[72px] items-center justify-between gap-4 border-t border-line px-5 py-3.5 first:border-t-0">
    <div className="min-w-0">
      <div className="text-[13.5px] font-bold text-ink">{title}</div>
      <div className="mt-0.5 text-[12.5px] text-ink-muted">{detail}</div>
    </div>
    <Switch on={on} />
  </div>
);

/* ── AI performance ──────────────────────────────────────────────────────── */

const PRINCIPLES = [
  { kicker: 'Protected', title: 'Workflows & sources', detail: 'Tools · permissions · owners' },
  { kicker: 'Routing', title: 'Approved pool', detail: 'Policy controlled' },
  { kicker: 'Release', title: 'Evidence required', detail: 'Evaluation · rollout · fallback' },
];

export const AiPerformanceSection: React.FC = () => {
  // const navigate = useNavigate();
  return (
    <div>
      <div className="mb-3.5 flex items-start justify-between gap-5 rounded-[14px] border border-line px-6 py-5" style={{ background: 'linear-gradient(135deg, #ffffff, #f3f3f1)' }}>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-dim">Current policy</div>
          <h2 className="m-0 mt-1.5 text-[23px] font-bold leading-[1.22] tracking-[-0.03em] text-ink">Client-approved routes only</h2>
        </div>
        <span className="shrink-0 rounded-lg px-2.5 py-1.5 text-[12px] font-bold text-ink">Protected</span>
      </div>

      <div className="mb-3.5 flex flex-col gap-2 sm:flex-row">
        {PRINCIPLES.map((p) => (
          <article key={p.kicker} className="flex min-h-[126px] min-w-0 flex-1 flex-col gap-1.5 rounded-panel border border-line bg-white p-4">
            <small className={KICKER}>{p.kicker}</small>
            <strong className="text-[14px] font-bold text-ink">{p.title}</strong>
            <p className="m-0 text-[12.5px] leading-[19px] text-ink-muted">{p.detail}</p>
          </article>
        ))}
      </div>

      <label className="mb-3.5 block rounded-panel border border-line bg-[#fafafa] p-4">
        <span className="block text-[13px] font-semibold text-ink">Default policy</span>
        <select
          disabled
          value="Client-approved routes only"
          className="mt-2 h-11 w-full rounded-card border border-line bg-canvas-inset px-3 text-[13px] text-ink-muted"
        >
          <option>Client-approved routes only</option>
        </select>
        <span className="mt-2 block text-[12px] text-ink-muted">High-risk work may use a fixed approved route</span>
      </label>

      <div className="overflow-hidden rounded-[13px] border border-line bg-white">
        <SwitchRow title="Require representative evaluation" detail="Work-specific quality and policy checks" on />
        <SwitchRow title="Keep approved fallback" detail="Previous approved route" on />
      </div>

      <div className="mt-[18px] flex flex-wrap items-center gap-3">
        {/* <Action size="lg" onClick={() => navigate('/partner/context?tab=sources')}>View AI capabilities</Action> */}
        <span className="text-[12.5px] text-ink-dim">Policy managed by the client's Admin / Builder.</span>
      </div>
    </div>
  );
};

/* ── People & departments ────────────────────────────────────────────────── */

/*
 * The owners of every client this partner looks after: one row per client,
 * with the company, its accountable person, and where they are. It is the
 * partner's own client list, so it stays inside the boundary the guide draws.
 */
const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2);
  return parts[0][0] + parts[1][0];
};

const ownerOf = (client: PartnerClientRow): string => client.contact_name || client.contact_email || 'No owner named yet';

const OwnerRow: React.FC<{ client: PartnerClientRow; onOpen: () => void }> = ({ client, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex min-h-[74px] w-full items-center gap-3 border-t border-line px-4 py-3 text-left transition-colors first:border-t-0 hover:bg-[#fbfbfa]"
  >
    <span className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-md bg-canvas-inset text-[12px] font-bold text-ink">
      {initialsOf(ownerOf(client))}
    </span>
    <span className="min-w-0 flex-1">
      <strong className="block truncate text-[13.5px] font-bold text-ink">{ownerOf(client)}</strong>
      <small className="block truncate text-[12.5px] text-ink-muted">Client owner · {client.name}</small>
    </span>
    <StatusChip status={STATUS_CHIP[client.status]} label={STATUS_LABEL[client.status]} />
    <ArrowRight size={14} className="shrink-0 text-ink-soft" />
  </button>
);

const FactCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex min-w-[200px] flex-1 basis-[calc(50%-6px)] flex-col gap-2 rounded-[13px] border border-line bg-canvas px-4 py-4">
    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-dim">{label}</span>
    <strong className="text-[13.5px] font-bold leading-[1.35] text-ink">{value}</strong>
  </div>
);

const OwnerDrawer: React.FC<{ client: PartnerClientRow; onClose: () => void }> = ({ client, onClose }) => (
  <Drawer
    layer="modal"
    onClose={onClose}
    kicker={<Kicker>Person & ownership</Kicker>}
    title={ownerOf(client)}
  >
    <div className="flex flex-wrap gap-3">
      <FactCard label="Established role" value="Client owner" />
      <FactCard label="Team" value={client.name} />
      <FactCard label="Decision boundary" value="Scope, audience, sources, and release" />
      <FactCard label="Access review" value={STATUS_LABEL[client.status]} />
    </div>
    <div className="-mx-5 mt-6 flex justify-end border-t border-line px-5 pt-4 sm:-mx-[30px] sm:px-[30px]">
      <Action variant="quiet" onClick={onClose}>Close</Action>
    </div>
  </Drawer>
);

export const PeopleSection: React.FC = () => {
  const [selected, setSelected] = useState<PartnerClientRow | null>(null);
  const overview = useAsync<PartnerOverview>(
    () => aaiApi.getPartnerOverview(), 'Could not load your clients.', [],
  );

  if (overview.loading) return <Loading label="Loading people" />;
  if (overview.error || !overview.data) return <ErrorNote message={overview.error || 'Could not load your clients.'} />;

  const owners = overview.data.clients;

  return (
    <div>
      <SectionIntro
        title={`${owners.length} ${owners.length === 1 ? 'person' : 'people'}`}
        body="The owner of every client you look after. Open a row to see their role and where the client is."
      />
      <div className="overflow-hidden rounded-[13px] border border-line bg-white">
        {owners.length === 0 && (
          <p className="m-0 px-4 py-5 text-[13px] text-ink-muted">No clients yet. Add a client and its owner appears here.</p>
        )}
        {owners.map((client) => <OwnerRow key={client.id} client={client} onOpen={() => setSelected(client)} />)}
      </div>
      {selected && <OwnerDrawer client={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

/* ── Roles & access ──────────────────────────────────────────────────────── */

const ROLE_RULES = [
  { role: 'Employee', sees: 'Own work, approved Quick Wins, joined Delivery Projects, personal results', decides: 'Own prompts, sharing, and result acceptance' },
  { role: 'Department Head', sees: 'Department work, protected patterns, approvals, and outcomes', decides: 'Department audience, operational outcome, and assigned Delivery Project gates' },
  { role: 'AI Champion', sees: 'Portfolio, protected patterns, reuse candidates, and capability evidence', decides: 'Route fit, company reuse, and adoption support' },
  { role: 'Executive', sees: 'Strategy, value, material risk, readiness, and decision evidence', decides: 'Priority, spend limit, and material risk' },
  { role: 'Financial Officer', sees: 'Approved budgets, quotes, commitments, invoices, value, and financially relevant protected aggregates', decides: 'Commercial envelope and funding conditions only when explicitly assigned' },
  { role: 'Admin / Builder', sees: 'Technical controls, scoped connection metadata, tests, routes, and releases', decides: 'Technical release and rollback inside approved policy' },
  { role: 'Solution Partner', sees: 'Assigned client work and client-approved sources only', decides: 'Delivery recommendations; never client scope or release' },
];

type RoleRule = typeof ROLE_RULES[number];

const clientDataFor = (rule: RoleRule): string => {
  if (rule.role === 'Solution Partner') return 'Active client only';
  return 'Not available unless explicitly assigned';
};

const RoleRow: React.FC<{ rule: RoleRule; onOpen: () => void }> = ({ rule, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex min-h-[74px] w-full items-center gap-3 border-t border-line px-4 py-3 text-left transition-colors first:border-t-0 hover:bg-[#fbfbfa]"
  >
    <strong className="w-[150px] shrink-0 text-[13.5px] font-bold text-ink">{rule.role}</strong>
    <span className="min-w-0 flex-1 text-[12.5px] leading-[18px] text-ink-muted">{rule.sees}</span>
    <ArrowRight size={14} className="shrink-0 text-ink-soft" />
  </button>
);

const RoleDrawer: React.FC<{ rule: RoleRule; onClose: () => void }> = ({ rule, onClose }) => (
  <Drawer
    layer="modal"
    onClose={onClose}
    kicker={<Kicker>Role & access</Kicker>}
    title={rule.role}
  >
    <div className="flex flex-wrap gap-3">
      <FactCard label="Can see" value={rule.sees} />
      <FactCard label="Can decide" value={rule.decides} />
      <FactCard label="Private prompts" value="Never visible through role alone" />
      <FactCard label="Client data" value={clientDataFor(rule)} />
    </div>
    <div className="-mx-5 mt-6 flex justify-end border-t border-line px-5 pt-4 sm:-mx-[30px] sm:px-[30px]">
      <Action variant="quiet" onClick={onClose}>Close</Action>
    </div>
  </Drawer>
);

export const RolesSection: React.FC = () => {
  const [selected, setSelected] = useState<RoleRule | null>(null);
  return (
    <div>
      <SectionIntro
        title={`${ROLE_RULES.length} established roles`}
        body="A role sets the default view. Delivery Project membership, Workspace membership, and source access are checked separately."
      />
      <div className="overflow-hidden rounded-[13px] border border-line bg-white">
        {ROLE_RULES.map((rule) => <RoleRow key={rule.role} rule={rule} onOpen={() => setSelected(rule)} />)}
      </div>
      {selected && <RoleDrawer rule={selected} onClose={() => setSelected(null)} />}
      <GrantsPanel className="mt-5" />
    </div>
  );
};

/* ── Tools ───────────────────────────────────────────────────────────────── */

/*
 * The client's tools, as the partner reads them.
 *
 * Connectors above is the partner's own catalog: what THEIR assignment
 * reaches. This is the client's: every tool a connection can be held to in
 * their workspace, whether they run on it, who in the company may reach it, and
 * what a skill would lose if it went away. A partner recommending work needs to
 * know what the client already has before recommending they buy something else.
 *
 * Read-only, like everything else in this modal except the framework. Who a
 * tool is given to is the client owner's decision, taken in their own
 * workspace, and showing a control here that the server would refuse would be
 * worse than showing none.
 */

const assignmentLine = (tool: OrbitToolRow): string => {
  if (tool.assignment === 'all') return 'Every department';
  if (tool.assignment === 'selected' && tool.assigned_departments.length > 0) {
    return tool.assigned_departments.join(', ');
  }
  if (tool.assignment === 'selected') return 'Named departments';
  return 'Nobody yet';
};

const connectionLine = (tool: OrbitToolRow): string => {
  if (!tool.connector) return 'Not a connection this platform can hold';
  if (tool.connector.organization_status === 'connected') return 'Connected for the company';
  if (tool.connector.personal_status === 'connected') return 'Connected by a person';
  return 'Not connected';
};

const ToolRow: React.FC<{ tool: OrbitToolRow; onOpen: () => void }> = ({ tool, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex min-h-[74px] w-full items-center gap-3 border-t border-line px-4 py-3 text-left transition-colors first:border-t-0 hover:bg-[#fbfbfa]"
  >
    <span className="min-w-0 flex-1">
      <span className="flex flex-wrap items-center gap-2">
        <strong className="text-[13.5px] font-bold text-ink">{tool.label}</strong>
        {tool.on_stack && <span className="rounded-full bg-canvas-inset px-2 py-0.5 text-[11px] font-semibold text-ink-muted">On their stack</span>}
      </span>
      <span className="mt-0.5 block text-[12.5px] text-ink-muted">{tool.category_label} · {connectionLine(tool)}</span>
    </span>
    <span className="hidden w-[190px] shrink-0 text-[12.5px] text-ink-muted sm:block">{assignmentLine(tool)}</span>
    <ArrowRight size={14} className="shrink-0 text-ink-soft" />
  </button>
);

const ToolDrawer: React.FC<{ tool: OrbitToolRow; onClose: () => void }> = ({ tool, onClose }) => {
  let skills = 'No skill declares it';
  if (tool.used_in_skills.length > 0) skills = tool.used_in_skills.map((s) => s.name).join(', ');
  let named = 'Not named in the company answers';
  if (tool.on_stack) named = tool.named_by || 'Named as part of their stack';
  return (
    <Drawer
      layer="modal"
      onClose={onClose}
      kicker={<Kicker>Tool</Kicker>}
      title={tool.label}
    >
      <div className="flex flex-wrap gap-3">
        <FactCard label="Category" value={tool.category_label} />
        <FactCard label="Connection" value={connectionLine(tool)} />
        <FactCard label="Available to" value={assignmentLine(tool)} />
        <FactCard label="On their stack" value={named} />
        <FactCard label="Used by skills" value={skills} />
      </div>
      <div className="-mx-5 mt-6 flex justify-end border-t border-line px-5 pt-4 sm:-mx-[30px] sm:px-[30px]">
        <Action variant="quiet" onClick={onClose}>Close</Action>
      </div>
    </Drawer>
  );
};

export const ToolsSection: React.FC = () => {
  const [selected, setSelected] = useState<OrbitToolRow | null>(null);
  const [query, setQuery] = useState('');
  const board = useAsync<OrbitToolBoard>(() => aaiApi.getOrbitTools(), 'Could not load this client’s tools.', []);

  if (board.loading) return <Loading label="Loading tools" />;
  if (board.error || !board.data) return <ErrorNote message={board.error || 'Could not load this client’s tools.'} />;

  const needle = query.trim().toLowerCase();
  const tools = board.data.tools.filter((t) => !needle || `${t.label} ${t.category_label}`.toLowerCase().includes(needle));
  const onStack = board.data.tools.filter((t) => t.on_stack).length;

  return (
    <div>
      <SectionIntro
        title={`${board.data.tools.length} ${board.data.tools.length === 1 ? 'tool' : 'tools'} they can reach`}
        body={`${onStack} of them the company named as part of how it already works. Open a row to see who it is available to and what would lose it.`}
      />
      <div className="mb-4 flex h-10 items-center gap-2 rounded-act border border-line bg-white px-3">
        <Search size={15} className="shrink-0 text-ink-soft" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search tools"
          aria-label="Search this client's tools"
          className="w-full border-0 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-soft"
        />
      </div>
      <div className="overflow-hidden rounded-[13px] border border-line bg-white">
        {tools.length === 0 && (
          <p className="m-0 px-4 py-5 text-[13px] text-ink-muted">
            {needle ? `No tool matches "${query}".` : 'No tools yet. They appear once the catalogue loads for this client.'}
          </p>
        )}
        {tools.map((tool) => <ToolRow key={tool.key} tool={tool} onOpen={() => setSelected(tool)} />)}
      </div>
      {board.data.unreachable.length > 0 && (
        <p className="m-0 mt-4 text-[12.5px] leading-[19px] text-ink-muted">
          Named by the company but not something this platform can connect: {board.data.unreachable.join(', ')}.
          Nothing can be built on those from here.
        </p>
      )}
      {selected && <ToolDrawer tool={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};
